/**
 * Event Stream Widget
 *
 * Scrolling real-time event log with color-coded event types.
 */

import { useMemo } from "react";
import { Radio } from "lucide-react";
import type { EventLogEntry } from "../../types/metrics";
import { useAppSelector } from "../../store/store";
import { generateTraces } from "../../demo/generators";

interface EventStreamWidgetProps {
  events: EventLogEntry[];
}

const SEVERITY_COLORS: Record<EventLogEntry["severity"], string> = {
  info: "text-accent-blue",
  warning: "text-accent-yellow",
  error: "text-accent-red",
  success: "text-accent-green",
};

const TYPE_LABELS: Record<EventLogEntry["type"], string> = {
  input: "INPUT",
  pii: "PII",
  guardrail: "GUARD",
  classify: "CLASS",
  retrieve: "RETRV",
  llm: "LLM",
  review: "REVIEW",
  storage: "STORE",
  embedding: "EMBED",
  recommend: "RECOM",
  error: "ERROR",
  feedback: "FBACK",
};

const TYPE_COLORS: Record<EventLogEntry["type"], string> = {
  input: "text-accent-cyan",
  pii: "text-accent-yellow",
  guardrail: "text-accent-purple",
  classify: "text-accent-blue",
  retrieve: "text-accent-blue",
  llm: "text-accent-orange",
  review: "text-accent-cyan",
  storage: "text-accent-green",
  embedding: "text-accent-purple",
  recommend: "text-accent-cyan",
  error: "text-accent-red",
  feedback: "text-accent-purple",
};

export function EventStreamWidget({ events }: EventStreamWidgetProps) {
  const selectedTraceId = useAppSelector((s) => s.dashboard.selectedTraceId);

  // Look up the correlated noteId from the selected trace
  const correlatedNoteId = useMemo(() => {
    if (!selectedTraceId) return null;
    // Generate traces to find the matching noteId (stable data, same seed)
    const traces = generateTraces();
    const match = traces.find((t) => t.traceId === selectedTraceId);
    return match?.noteId ?? null;
  }, [selectedTraceId]);

  return (
    <div className="widget h-full flex flex-col">
      <div className="widget-header">
        <div className="flex items-center gap-2">
          <Radio className="w-3.5 h-3.5 text-accent-cyan" />
          <h3 className="text-xs font-semibold text-text-primary">
            Event Stream
          </h3>
        </div>
        <span className="text-xs text-text-muted">{events.length} events</span>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-0.5 font-mono text-xs">
        {events.length === 0 ? (
          <div className="flex items-center justify-center h-full text-text-muted text-xs">
            Waiting for events...
          </div>
        ) : (
          events.map((event) => {
            const isCorrelated =
              correlatedNoteId != null && event.noteId === correlatedNoteId;
            return (
              <div
                key={event.id}
                className={`flex items-start gap-2 px-2 py-1 rounded hover:bg-bg-hover transition-colors animate-slide-in ${
                  isCorrelated
                    ? "border-l-2 border-accent-orange bg-bg-hover/30"
                    : ""
                }`}
              >
                <span className="text-text-muted shrink-0 w-14">
                  {new Date(event.timestamp).toLocaleTimeString("en-US", {
                    hour12: false,
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })}
                </span>
                <span
                  className={`shrink-0 w-12 font-semibold ${TYPE_COLORS[event.type]}`}
                >
                  {TYPE_LABELS[event.type]}
                </span>
                <span className={SEVERITY_COLORS[event.severity]}>
                  {event.message}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
